/**
 * dunning-block-store — drives the single global <DunningCreateBlockedDialog/>
 * mounted in AppShell.
 *
 * When a limited/suspended/review account tries to CREATE new content, the create
 * entry points (useOpenAppModal.processApp) open this dialog instead of launching
 * the editor. One dialog, one source of truth — memory-only transient UI state,
 * intentionally NOT persisted.
 *
 * Why a client-side store and not a server guard: the Designer persists through
 * its OWN out-of-process API, so open-api's DunningEnforcementGuard never sees a
 * design create/save. Blocking the *entry point* here is the only place a designer
 * create can be stopped — and it maps the ladder's "no NEW content" wording more
 * exactly than blocking every save would (edits of existing content stay allowed).
 */
import { create } from 'zustand';

/**
 * Why the dialog is open:
 * - `create` — a create entry point stopped the customer BEFORE anything was sent
 *   (the Designer path open-api cannot see).
 * - `write`  — the server refused a write with ACCOUNT_LIMITED (pairing a screen,
 *   inviting a user, publishing). Different copy: it was not only new content.
 */
export type DunningBlockReason = 'create' | 'write';

interface DunningBlockState {
  /** True opens the dialog. */
  isOpen: boolean;
  reason: DunningBlockReason;
  open: (reason?: DunningBlockReason) => void;
  close: () => void;
}

export const useDunningBlockStore = create<DunningBlockState>((set) => ({
  isOpen: false,
  reason: 'create',
  open: (reason = 'create') => set({ isOpen: true, reason }),
  close: () => set({ isOpen: false }),
}));
